<template>
    <div class="trainer-bag">
        <div class="trainer-bag__tabs">
            <div
                v-for="mark in bookmarks"
                :key="mark"
                class="trainer-bag__tab btn-shadow"
                :class="{'accent': bookmark === mark}"
                @click="goToTab(mark)"
            >
                <span class="trainer-bag__tab-label-wrap">
                    <span class="trainer-bag__tab-label">{{ tabLabels[mark] }}</span>
                    <v-icon v-if="tabHasStar(mark)" size="12" class="trainer-bag__tab-star">mdi-star</v-icon>
                </span>
            </div>
        </div>

        <Swiper
            ref="tabSwiper"
            :key="bookmarks.join(',')"
            class="trainer-bag__swiper"
            :options="tabSwiperOption"
            :style="{height: tabAreaHeight + 'px'}"
            @slideChange="onTabSlideChange"
        >
            <swiper-slide v-for="mark in bookmarks" :key="mark">
                <v-card-text v-if="mark==='Trainer'" class="font-weight-medium trainer-stats-adaptive-text pa-2 trainer-bag__pane">
                    <div class="trainer-bag__actions">
                        <span class="trainer-bag__skills-btn-wrap">
                            <v-tooltip v-if="freeSkillpoints > 0" top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" size="18" class="trainer-bag__skills-star">mdi-star</v-icon>
                                </template>
                                <span>{{ $t('trainer.trainerBag.canBuySkillTooltip') }}</span>
                            </v-tooltip>
                            <v-btn
                                rounded
                                small
                                class="skin-btn-skills"
                                @click="showSkills = true"
                            > {{ $t('trainer.trainerBag.skillsButton') }}</v-btn>
                        </span>
                    </div>

                    <!-- ma-0 nutne - Vuetify vychozi zaporne okraje radku uvnitr Swiperu (pevna sirka slidu na pixel) zpusobovaly zalamovani sloupcu 7+5 pod sebe -->
                    <v-row class="pa-0 ma-0" dense>
                        <v-col cols="7">
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.level') }}</v-col>
                                <v-col cols="3">{{ trainer.experience.level }}</v-col>
                            </v-row>
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.experience') }}</v-col>
                                <v-col cols="3">{{ trainer.experience.exp }}</v-col>
                            </v-row>
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.nextLvlExp') }}</v-col>
                                <v-col cols="3">{{ trainer.experience.nextLevelExp }}</v-col>
                            </v-row>
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.freeSkillPoints') }}</v-col>
                                <v-col cols="3">{{ freeSkillpoints }}</v-col>
                            </v-row>
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.pokeballs') }}</v-col>
                                <v-col cols="3" class="d-flex justify-space-between">{{ pokeballs }} <v-icon size="25" style="position:relative;top: -2px; right: 0px">mdi-pokeball</v-icon></v-col>
                            </v-row>
                            <v-row dense class="ma-0">
                                <v-col cols="9">{{ $t('trainer.trainerBag.stats.masterballs') }}</v-col>
                                <v-col cols="3" class="d-flex justify-space-between">{{ trainer.masterballs }} <div style="position:relative;top: -2px; right: 0px"><v-icon size="25" class="ma-0 pa-0">mdi-baseball</v-icon></div></v-col>
                            </v-row>
                        </v-col>
                        <v-col cols="5" class="d-flex align-center justify-center">
                            <!-- Level bublina v rohu portrétu trenéra (zadání
                                 uživatele 2026-08-28) - stejný jednodílný SVG
                                 odznak jako PokemonPickerCard.vue/
                                 TeamBottomSheet.vue, jen velikostně v %
                                 vzhledem k obrázku (ne pevný px), ať zůstane
                                 poměrově malá bez ohledu na šířku sloupce. -->
                            <div class="trainer-bag__portrait-wrap">
                                <v-img
                                    v-if="trainer.img"
                                    :src="require(`@/assets/pic/trainer/${trainer.img}`)"
                                    contain
                                    max-height="230px"
                                    width="100%"
                                />
                                <svg v-if="trainer.img" class="trainer-bag__portrait-level" viewBox="0 0 24 24">
                                    <defs>
                                        <radialGradient :id="trainerLevelGradientId" cx="30%" cy="30%" r="75%">
                                            <stop offset="0%" stop-color="#ffffff" />
                                            <stop offset="100%" stop-color="#494949" />
                                        </radialGradient>
                                    </defs>
                                    <circle cx="12" cy="12" r="12" :fill="`url(#${trainerLevelGradientId})`" />
                                    <path
                                        d="M12,1L14.53,8.52L22.46,8.6L16.09,13.33L18.47,20.9L12,16.3L5.53,20.9L7.91,13.33L1.54,8.6L9.47,8.52Z"
                                        fill="#ffebbc"
                                    />
                                    <text x="12" y="16.3" text-anchor="middle" :font-size="trainerLevelFontSize" class="trainer-bag__portrait-level-text">{{ trainer.experience.level }}</text>
                                </svg>
                            </div>
                        </v-col>
                    </v-row>

                    <template v-if="trainer.badges.map(x => x.id).length > 0">
                        <v-divider class="my-3" />
                        <Badges
                            :badges="trainer.badges.map(x => x.id)"
                            :size="60"
                            @badge-click="openBadgeDetail"
                        />
                    </template>
                </v-card-text>

                <v-card-text v-if="mark==='Edit Profile'" class="pa-2">
                    <EditProfileForm :trainer="trainer" :active="bookmark === 'Edit Profile'" @layout-changed="onTabContentResized" />
                </v-card-text>

                <v-card-text v-if="mark==='Pokemons'" class="pa-2">
                    <PokemonSwiper
                        class="swiper"
                        :data="tempPokemons"
                        :updater="updater"
                        :stones="eeveeStones"
                        :effect-items="effectItems"
                        :in-fight="inFight"
                        :delete-pokemon-callback="deletePokemonCallback"
                        :active="bookmark === 'Pokemons'"
                        @layout-changed="onTabContentResized"
                        @item-use-error="itemUseErrorMessage = $event"
                    />
                </v-card-text>

                <v-card-text v-if="mark==='Achievements'" class="pa-2 trainer-bag__pane" @click="openAchieveInfo = null">

                            <div class="achieve-list ma-1 pa-2 chip-dekorator-dark-small">
                                <div v-for="item in achieves" :key="item.id" class="achieve-row">
                                    <div
                                        class="achieve-row__trigger"
                                        :class="{'achieve-row__trigger--active': openAchieveInfo === item.id}"
                                        @click.stop="toggleAchieveInfo(item.id)"
                                    >
                                        <div class="achieve-row__name-line">
                                            <span class="achieve-row__name text-bold font-weight-bold">{{ item.name }}</span>
                                        </div>
                                        <div class="achieve-row__meta-line">
                                            <span
                                                v-if="item.lvl !== null && item.lvl !== undefined  && item.maxLvl !== null && item.maxLvl !== undefined"
                                                class="achieve-row__stars"
                                            >
                                                <v-icon
                                                    v-for="item in item.lvl"
                                                    :key="'reached-trainerdex-'+item"
                                                    :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey darken-3'"
                                                >
                                                    mdi-star
                                                </v-icon>
                                                <v-icon
                                                    v-for="item in (item.maxLvl-item.lvl)"
                                                    :key="'trainerdex-'+item"
                                                    :color="$vuetify.theme.dark ? 'grey lighten-3' : 'grey'"
                                                >
                                                    mdi-star-outline
                                                </v-icon>
                                            </span>
                                            <span class="achieve-row__value">
                                                <template v-if="item.next !== null && item.next !== undefined">{{ item.value}}/{{item.next }}</template>
                                                <template v-else>{{ item.value}}</template>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                </v-card-text>

                <v-card-text v-if="mark==='Stones'" class="pa-2 trainer-bag__pane">
                    <Stones
                        :size="80"
                        :reload-page="reloadPage"
                        :temp-pokemons="tempPokemons"
                        :items="stones"
                        @locked-stone-click="lockedStone = $event"
                    />
                </v-card-text>

                <v-card-text v-if="mark==='Items'" class="pa-2 trainer-bag__pane">
                    <Items
                        :reload-page="reloadPage"
                        :temp-pokemons="tempPokemons"
                        :items="effectItems"
                        :in-fight="inFight"
                        @item-use-error="itemUseErrorMessage = $event"
                    />
                </v-card-text>
            </swiper-slide>
        </Swiper>

        <div
            v-if="badgeDetail"
            class="oak-popup-overlay"
            @click="closeBadgeDetail"
        >
            <div class="oak-popup-box">
                <div class="oak-popup-box__title">{{ $t('common.badgeNames.' + badgeDetail.badgeName) }}:</div>
                <div class="oak-popup-box__body">
                    <v-img
                        v-if="badgeDetail.trainerImg"
                        :src="require(`@/assets/pic/trainer/${badgeDetail.trainerImg}`)"
                        max-width="70"
                        max-height="70"
                        contain
                        style="flex: 0 0 auto;"
                    />
                    <v-img
                        :src="require(`@/assets/pic/badge/${selectedBadgeId}.png`)"
                        max-width="50"
                        max-height="50"
                        contain
                        style="flex: 0 0 auto;"
                    />
                    <div class="oak-popup-box__text">
                        <p v-if="badgeDetail.trainerName">{{ $t('trainer.trainerBag.badgeDetail.wonFromPrefix') }} {{ badgeDetail.trainerName }}</p>
                        <p v-if="badgeDetail.cityName">{{ $t('trainer.trainerBag.badgeDetail.inCityPrefix') }} {{ $t('common.cityNames.' + badgeDetail.cityName) }}</p>
                        <p v-if="badgeDetail.day !== null && badgeDetail.day !== undefined">{{ $t('trainer.trainerBag.badgeDetail.onDay', {day: badgeDetail.day}) }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="openAchieveInfoItem"
            class="oak-popup-overlay"
            @click="openAchieveInfo = null"
        >
            <div class="oak-popup-box">
                <div class="oak-popup-box__title">{{ openAchieveInfoItem.name }}:</div>
                <div class="oak-popup-box__body">
                    <v-img
                        src='@/assets/pic/web/oak_avatar.png'
                        max-width="60"
                        max-height="60"
                        style="transform: scaleX(-1); flex: 0 0 auto;"
                    />
                    <div class="oak-popup-box__text">
                        <p v-for="(msg, i) in openAchieveInfoItem.messages" :key="i">{{ msg }}</p>
                    </div>
                </div>
            </div>
        </div>

        <ResponsiveDialog
            v-if="lockedStone"
            v-model="showLockedStoneDialog"
            :max-width="380"
            :full-height="false"
            :header-title="lockedStone.name"
            header-class="skin-window-header--oak"
            card-class="skin-card"
        >
            <div class="skin-window--oak locked-stone-dialog-bg">
                <v-card-text class="transparent03 text-center locked-stone-dialog-content">
                    <p>{{ $t('trainer.stones.lockedInTradePopup') }}</p>
                    <v-btn
                        v-if="isInCity"
                        :loading="cancelingLockedStoneOffer"
                        color="warning black--text"
                        @click="cancelLockedStoneOffer"
                    >
                        {{ $t('trainer.stones.cancelOfferButton') }}
                    </v-btn>
                </v-card-text>
            </div>
        </ResponsiveDialog>

        <ConfirmDialog ref="confirmDialog"/>

        <!-- stejny duvod jako showLockedStonePopup vyse - musi byt mimo Swiper (viz Items.vue) -->
        <OakSkillHintPopup
            v-if="itemUseErrorMessage"
            icon="mdi-alert"
            danger
            roomy
            :text="itemUseErrorMessage"
            @close="itemUseErrorMessage = null"
        />

        <ResponsiveDialog v-model="showSkills" :max-width="820" color="warning" header-icon="⭐" :header-title="$t('trainer.trainerBag.skillsDialog.title')" header-class="skin-window-header--trainerskills" card-class="skin-card">
                <v-card-text class="justify-center text-center skill-dialog-scroll skill-dialog-fixed-text skin-window--trainerskills" @click="openSkillInfo = null">
                    <div class="skill-dialog-stats">
                        <span class="skill-dialog-stats__label">{{ $t('trainer.trainerBag.stats.freeSkillPoints') }}</span>
                        <span class="skill-dialog-stats__value">{{freeSkillpoints}}</span>
                        <span class="skill-dialog-stats__label">{{ $t('trainer.trainerBag.stats.level') }}</span>
                        <span class="skill-dialog-stats__value">{{trainer.experience.level}}</span>
                    </div>
                    <div class="skill-hint-text">
                        <p class="ma-0">{{ $t('trainer.trainerBag.skillsDialog.hint1') }}</p>
                        <p class="ma-0">{{ $t('trainer.trainerBag.skillsDialog.hint2') }}</p>
                    </div>
                    <div class="skill-buy-list">
                        <div v-for="(skill, index) in trainerSkills" :key="skill.name" class="skill-buy-row opacity-less">
                            <div
                                class="skill-buy-row__clickable"
                                :class="{'skill-buy-row__clickable--active': openSkillInfo === skill.name}"
                                @click.stop="toggleSkillInfo(skill.name)"
                            >
                                <span class="skill-buy-row__name">{{ $t('trainer.trainerSkills.' + index + '.name') }}</span>
                                <span class="skill-buy-row__icon">
                                    <v-icon size="24">{{ skill.icon }}</v-icon>
                                </span>
                                <span class="skill-buy-row__value">{{skill.value}}</span>
                            </div>
                            <span class="skill-buy-row__action" :class="{'skill-buy-row__action--left': rightHanded}">
                                <v-btn
                                    x-small
                                    :loading="isLoading"
                                    :disabled="!canBuy(skill.value)"
                                    class="text-center skin-btn-buy"
                                    rounded
                                    @click="buySkill(index)"
                                >
                                    {{ $t('trainer.trainerBag.skillsDialog.buyButton') }}
                                </v-btn>
                            </span>
                            <OakSkillHintPopup
                                v-if="openSkillInfo === skill.name"
                                :skill="{icon: skill.icon, name: $t('trainer.trainerSkills.' + index + '.name'), description: $t('trainer.trainerSkills.' + index + '.description')}"
                                @close="openSkillInfo = null"
                            />
                        </div>
                    </div>
                </v-card-text>
                <v-card-actions class="d-none d-md-flex">
                    <v-spacer></v-spacer>
                    <v-btn
                        color="warning black--text btn-shadow"
                        @click="showSkills = false"
                    >
                        {{ $t('trainer.trainerBag.skillsDialog.closeButton') }}
                    </v-btn>
                </v-card-actions>
        </ResponsiveDialog>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
    import 'swiper/swiper.scss';
    import Badges from "@/components/trainer/Badges";
    import Stones from "@/components/trainer/Stones";
    import Items from "@/components/trainer/Items";
    import PokemonSwiper from "@/components/pokemon/PokemonSwiper";
    import EditProfileForm from "@/components/user/EditProfileForm";
    import OakSkillHintPopup from "@/components/utils/OakSkillHintPopup";
    import ResponsiveDialog from "@/components/utils/ResponsiveDialog";
    import ConfirmDialog from "@/components/utils/ConfirmDialog";
    import {API_ENTRYPOINT} from "@/config/entrypoint";
    import {flags} from '@/config/featureFlags';
    import {TRAINER_SKILLS} from "@/utils/trainerSkillsEnum";
    import {Positions} from "@/utils/positionEnum";

    export default {
        components: {Badges, Stones, Items, PokemonSwiper, EditProfileForm, OakSkillHintPopup, ResponsiveDialog, ConfirmDialog, Swiper, SwiperSlide},
        props: {
            trainer: {type: Object, required: true},
            stones: {type: Array, default: () => []},
            effectItems: {type: Array, default: () => []},
            tempPokemons: {type: Array, default: () => []},
            pokeballs: {type: Number, default: 0},
            reloadPage: {type: Function, default: () => {}},
            updater: {type: Number, default: 0},
            eeveeStones: {type: Array, default: () => []},
            inFight: {type: Boolean, default: false},
            deletePokemonCallback: {type: Function, default: () => {}},
        },
        data() {
            return {
                bookmark: "Trainer",
                openAchieveInfo: null,
                selectedBadgeId: null,
                badgeDetail: null,
                openSkillInfo: null,
                showSkills: false,
                lockedStone: null,
                cancelingLockedStoneOffer: false,
                itemUseErrorMessage: null,
                // zmerena vyska "az do konce viditelne oblasti" - stejny princip jako PokemonSwiper.vue/EditProfileForm.vue, viz claude/components/trainer.md
                tabAreaHeight: 500,
                trainerSkills: JSON.parse(JSON.stringify(TRAINER_SKILLS)),
            }
        },
        computed: {
            ...mapGetters({
                isLoading: 'trainer/isLoading',
                pokemons: 'pokemon/items',
                ownTrainer: 'trainer/ownTrainer',
                freeSkillpoints: 'trainer/freeSkillpoints',
                position: 'journey/position',
            }),
            isInCity() {
                return !!this.position && this.position.type === Positions.CITY;
            },
            showLockedStoneDialog: {
                get() {
                    return !!this.lockedStone;
                },
                set(value) {
                    if (!value) {
                        this.lockedStone = null;
                    }
                },
            },
            hasEvolvablePokemon() {
                return this.pokemons.some(p =>
                    this.tempPokemons.includes(p.id) && p.evolvable && p.evolvable.length > 0
                );
            },
            hasSkillBuyPokemon() {
                return this.pokemons.some(p =>
                    this.tempPokemons.includes(p.id) && p.posibleBuySkill
                );
            },
            rightHanded() {
                return flags.rightHanded;
            },
            // Unikatni id pro SVG <radialGradient> (viz TeamBottomSheet.vue/
            // PokemonPickerCard.vue stejny vzor) - kdyby nekdy byla na
            // strance vic nez jedna instance TrainerBag.vue najednou.
            trainerLevelGradientId() {
                return 'trainer-bag-level-grad-' + this._uid;
            },
            // Font-size cisla levelu v SVG viewBox 0-24 jednotkach - stejne
            // hodnoty jako PokemonPickerCard.vue/TeamBottomSheet.vue.
            trainerLevelFontSize() {
                const len = String(this.trainer.experience.level).length;
                if (len <= 1) return 14;
                if (len === 2) return 11;
                return 8;
            },
            bookmarks() {
                let bookmarks = ["Trainer", "Pokemons", "Achievements"];
                if (this.stones.length > 0) {
                    bookmarks.push("Stones");
                }
                if (this.effectItems.length > 0) {
                    bookmarks.push("Items");
                }
                bookmarks.push("Edit Profile");
                return bookmarks;
            },
            // mapovani interniho identifikatoru (pouziva se i pro v-if/porovnavani zalozek) na prelozeny popisek - $t() uvnitr, musi byt computed
            tabLabels() {
                return {
                    'Trainer': this.$t('trainer.trainerBag.tabs.trainer'),
                    'Pokemons': this.$t('trainer.trainerBag.tabs.pokemons'),
                    'Achievements': this.$t('trainer.trainerBag.tabs.achievements'),
                    'Stones': this.$t('trainer.trainerBag.tabs.stones'),
                    'Items': this.$t('trainer.trainerBag.tabs.items'),
                    'Edit Profile': this.$t('trainer.trainerBag.tabs.editProfile'),
                };
            },
            // initialSlide podle aktualniho bookmarku, aby remount swiperu (viz :key, pri pridani/zmizeni "Stones") neskocil na prvni tab
            tabSwiperOption() {
                return {
                    loop: true,
                    speed: 300,
                    noSwipingClass: 'swiper-no-swiping',
                    initialSlide: Math.max(0, this.bookmarks.indexOf(this.bookmark)),
                };
            },
            openAchieveInfoItem() {
                if (!this.openAchieveInfo) {
                    return null;
                }
                return this.achieves.find(item => item.id === this.openAchieveInfo) || null;
            },
            // "id" je stabilni identifikator pro v-for/porovnavani (viz openAchieveInfo), "name"/"messages" jsou uz prelozene $t() - musi byt computed
            achieves() {
                const t = this.trainer;
                const reachNextLvlHint = this.$t('trainer.trainerBag.achievements.reachNextLvlHint');
                const pokemonCatcherHint = this.$t('trainer.trainerBag.achievements.pokemonCatcherHint');
                let achieves = [];
                achieves.push({
                    id: 'pokemonObserver',
                    name: this.$t('trainer.trainerBag.achievements.pokemonObserver.name'),
                    value: t.pokedex.length,
                    next: t.nextPokedexLvl,
                    lvl: t.pokedexLvl,
                    maxLvl: t.pokedexMaxLvl,
                    messages: [this.$t('trainer.trainerBag.achievements.pokemonObserver.hint'), reachNextLvlHint]
                });
                if (t.leagueChampion) {
                    achieves.push({
                        id: 'alolanPokemonObserver',
                        name: this.$t('trainer.trainerBag.achievements.alolanPokemonObserver.name'),
                        value: t.pokedexAlola.length,
                        next: t.nextPokedexLvlAlola,
                        lvl: t.pokedexLvlAlola,
                        maxLvl: t.pokedexMaxLvlAlola,
                        messages: [this.$t('trainer.trainerBag.achievements.alolanPokemonObserver.hint'), reachNextLvlHint]
                    });
                }
                achieves.push({
                    id: 'pokemonCatcherGen1',
                    name: this.$t('trainer.trainerBag.achievements.pokemonCatcherGen1.name'),
                    value: t.catchedTypesGen1.length,
                    next: t.nextCatchedPokemonTypesLvlGen1,
                    lvl: t.pokemonTypesAchieveLvlGen1,
                    maxLvl: t.pokemonTypesAchieveMaxLvlGen1,
                    messages: [pokemonCatcherHint, reachNextLvlHint]
                });
                if (t.leagueChampion) {
                    achieves.push({
                        id: 'pokemonCatcherAlola',
                        name: this.$t('trainer.trainerBag.achievements.pokemonCatcherAlola.name'),
                        value: t.catchedTypesAlola.length,
                        next: t.nextCatchedPokemonTypesLvlAlola,
                        lvl: t.pokemonTypesAchieveLvlAlola,
                        maxLvl: t.pokemonTypesAchieveMaxLvlAlola,
                        messages: [pokemonCatcherHint, reachNextLvlHint]
                    });
                }
                achieves.push({
                    id: 'trainerDevastator',
                    name: this.$t('trainer.trainerBag.achievements.trainerDevastator.name'),
                    value: t.trainerdex.length,
                    next: t.nextTrainerdexLvl,
                    lvl: t.trainerdexLvl,
                    maxLvl: t.trainerdexMaxLvl,
                    messages: [this.$t('trainer.trainerBag.achievements.trainerDevastator.hint'), reachNextLvlHint]
                });
                achieves.push({
                    id: 'trainerFightVictories',
                    name: this.$t('trainer.trainerBag.achievements.trainerFightVictories.name'),
                    value: t.wins,
                    next: null,
                    lvl: null,
                    maxLvl: null,
                    messages: [this.$t('trainer.trainerBag.achievements.trainerFightVictories.hint')]
                });
                return achieves;
            },
        },
        watch: {
            trainer: {
                immediate: true,
                handler(t) {
                    if (!t) {
                        return;
                    }
                    this.trainerSkills.turistic.value = t.trainerSkills.turistic;
                    this.trainerSkills.aim.value = t.trainerSkills.aim;
                    this.trainerSkills.pokefinding.value = t.trainerSkills.pokefinding;
                    this.trainerSkills.trainerIni.value = t.trainerSkills.trainerIni;
                    this.trainerSkills.pokeIni.value = t.trainerSkills.pokeIni;
                    this.trainerSkills.enduancer.value = t.trainerSkills.enduancer;
                    this.trainerSkills.dung.value = t.trainerSkills.dung;
                    this.trainerSkills.luck.value = t.trainerSkills.luck;
                },
            },
            // trainer prop nese počáteční hodnoty, ownTrainer je přepisuje po refreshi (např. po nákupu skillu).
            ownTrainer: {
                immediate: true,
                handler(t) {
                    if (!t) {
                        return;
                    }
                    this.trainerSkills.turistic.value = t.trainerSkills.turistic;
                    this.trainerSkills.aim.value = t.trainerSkills.aim;
                    this.trainerSkills.pokefinding.value = t.trainerSkills.pokefinding;
                    this.trainerSkills.trainerIni.value = t.trainerSkills.trainerIni;
                    this.trainerSkills.pokeIni.value = t.trainerSkills.pokeIni;
                    this.trainerSkills.enduancer.value = t.trainerSkills.enduancer;
                    this.trainerSkills.dung.value = t.trainerSkills.dung;
                    this.trainerSkills.luck.value = t.trainerSkills.luck;
                },
            },
        },
        created() {
            if (this.trainer && this.trainer.id) {
                this.loadOwnTrainerAction(this.trainer.id);
            }
        },
        mounted() {
            this.$nextTick(this.updateTabAreaHeight);
            window.addEventListener('resize', this.updateTabAreaHeight);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.updateTabAreaHeight);
        },
        methods: {
            ...mapActions({
                fetchObject: 'trainer/fetchObject',
                refreshPosition: 'journey/loadPosition',
                loadOwnTrainerAction: 'trainer/loadOwnTrainer',
                cancelTradeOfferAction: 'trainer/cancelTradeOffer',
            }),
            async cancelLockedStoneOffer() {
                if (!this.lockedStone || !this.lockedStone.openTradeOfferId) {
                    return;
                }
                const rlyCancel = await this.$refs.confirmDialog.open(this.$t('trade.hub.confirmCancel'));
                if (!rlyCancel) {
                    return;
                }
                this.cancelingLockedStoneOffer = true;
                this.cancelTradeOfferAction(this.lockedStone.openTradeOfferId).then(() => {
                    this.cancelingLockedStoneOffer = false;
                    this.lockedStone = null;
                    this.reloadPage();
                });
            },
            // visibleBottom ma misto pro TeamBottomSheet uz odectene pres CSS (.app-screen-bg padding-bottom) - tady jen maly zaokrouhlovaci buffer, ne dalsi rezerva
            updateTabAreaHeight() {
                const swiperEl = this.$refs.tabSwiper && this.$refs.tabSwiper.$el;
                const scrollContainer = swiperEl && swiperEl.closest('.trainer-detail-page__scroll');
                if (!swiperEl || !scrollContainer) {
                    return;
                }
                const top = swiperEl.getBoundingClientRect().top;
                const visibleBottom = scrollContainer.getBoundingClientRect().bottom;
                const next = Math.max(320, Math.floor(visibleBottom - top - 4));
                if (next === this.tabAreaHeight) {
                    return;
                }
                this.tabAreaHeight = next;
                this.$nextTick(() => {
                    const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                    if (swiper) {
                        swiper.update();
                    }
                });
            },
            goToTab(mark){
                if (this.bookmark === mark) {
                    return;
                }
                const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                if (swiper) {
                    swiper.slideToLoop(this.bookmarks.indexOf(mark));
                } else {
                    this.bookmark = mark;
                }
            },
            onTabContentResized(){
                const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                if (swiper) {
                    swiper.update();
                }
            },
            onTabSlideChange(){
                const swiper = this.$refs.tabSwiper && this.$refs.tabSwiper.$swiper;
                if (!swiper) {
                    return;
                }
                const mark = this.bookmarks[swiper.realIndex];
                if (mark !== undefined) {
                    this.bookmark = mark;
                }
            },
            tabHasStar(mark){
                if (mark === 'Trainer') {
                    return this.freeSkillpoints > 0;
                }
                if (mark === 'Pokemons') {
                    return this.hasEvolvablePokemon || this.hasSkillBuyPokemon;
                }
                return false;
            },
            toggleAchieveInfo(name){
                this.openAchieveInfo = this.openAchieveInfo === name ? null : name;
            },
            openBadgeDetail(badgeId){
                this.selectedBadgeId = badgeId;
                this.badgeDetail = null;
                const url = new URL("api/get-badge-detail", API_ENTRYPOINT);
                url.searchParams.set('badge', badgeId);
                url.searchParams.set('trainer', this.trainer.id);
                this.fetchObject({url, method: 'GET'}).then((data) => {
                    if (data !== undefined) {
                        this.badgeDetail = data;
                    }
                });
            },
            closeBadgeDetail(){
                this.badgeDetail = null;
                this.selectedBadgeId = null;
            },
            toggleSkillInfo(name){
                this.openSkillInfo = this.openSkillInfo === name ? null : name;
            },
            canBuy(lvl){
                return this.freeSkillpoints > 0 && lvl < this.trainer.experience.level;
            },
            buySkill(skill){
                const url = new URL('api/trainer-buy-skill', API_ENTRYPOINT);
                let objSkill = {trainerId: this.trainer.id, skillName: skill};
                let obj = {url: url, method:'POST', obj: objSkill};
                this.fetchObject(obj).then(
                    (data) => {
                        if(data !== undefined){
                            this.loadOwnTrainerAction(this.trainer.id);
                            if (this.trainer && this.trainer.position && this.trainer.position.id) {
                                this.refreshPosition(this.trainer.position.id);
                            }
                        }
                    }
                );
            },
        },
    }
</script>

<style scoped>
    .trainer-bag {
        width: 100%;
    }

    .trainer-bag__swiper {
        width: 100%;
    }

    /* Level bublina v rohu portrétu (zadání uživatele 2026-08-28) - velikost
       v % vzhledem k wrapperu (ne pevný px), ať zůstane poměrově malá bez
       ohledu na šířku sloupce/obrazovky. Stejný SVG odznak jako
       PokemonPickerCard.vue/TeamBottomSheet.vue. */
    .trainer-bag__portrait-wrap {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: center;
    }
    .trainer-bag__portrait-level {
        position: absolute;
        right: 6%;
        bottom: 6%;
        width: 22%;
        max-width: 40px;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        box-shadow: 1px 3px 6px rgba(243,224,184,0.6);
    }
    .trainer-bag__portrait-level-text {
        fill: #000;
        font-weight: 900;
    }

    /* neaktivni slide bez vlastniho overflow:hidden muze pretekat do sousedniho tabu (viz claude/components/trainer.md) */
    .trainer-bag__swiper .swiper-slide {
        overflow: hidden;
        /* swiper.scss nastavuje box-sizing:content-box, Vuetify dedi border-box - bez zastaveni tady pretekaly sloupce 7+5 uvnitr */
        box-sizing: border-box;
        height: 100%;
    }

    /* kratsi obsah necha prazdne misto dole misto "urizle" karty, delsi obsah scrolluje jen tady uvnitr (ne cela stranka) */
    .trainer-bag__pane {
        height: 100%;
        overflow-y: auto;
        box-sizing: border-box;
    }

    .theme--light .trainer-stats-adaptive-text {
        color: rgba(0, 0, 0, .87);
    }
    .theme--dark .trainer-stats-adaptive-text {
        color: rgba(255, 255, 255, .87);
    }

    .trainer-bag__tabs {
        container-type: inline-size;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 10px 12px 6px;
    }

    .trainer-bag__tab {
        position: relative;
        flex: 1 1 84px;
        min-width: 84px;
        max-width: 160px;
        text-align: center;
        padding: 6px 4px;
        border-radius: 6px;
        background-color: rgba(0, 0, 0, .06);
        border: 1px solid rgba(0, 0, 0, .1);
        cursor: pointer;
        font-weight: 600;
        font-size: clamp(10px, 3.1cqi, 14px);
    }

    /* inline-block shrink-to-fit presne na sirku textu, aby .trainer-bag__tab-star sedela na rohu textu, ne cele pilulky */
    .trainer-bag__tab-label-wrap {
        position: relative;
        display: inline-block;
        max-width: 100%;
        vertical-align: middle;
    }

    /* overflow/ellipsis patri jen textu, ne obalu vyse - jinak by orizaval i absolutne pozicovanou hvezdicku vyčnivajici za okraj */
    .trainer-bag__tab-label {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* !important na barve i position - Vuetify ".v-icon.v-icon" ma vyssi specificitu a jinak prebije obojí zpet na default */
    .trainer-bag__tab-star {
        position: absolute !important;
        top: -8px;
        left: -10px;
        z-index: 20;
        color: #FFD700 !important;
        filter: drop-shadow(0 0 3px rgba(0, 0, 0, .9)) drop-shadow(0 0 3px rgba(0, 0, 0, .9));
    }

    .trainer-bag__actions {
        display: flex;
        justify-content: flex-end;
        padding: 0 0 4px;
    }

    .trainer-bag__skills-btn-wrap {
        position: relative;
        display: inline-block;
    }

    /* !important na barve - viz .trainer-bag__tab-star vyse */
    .trainer-bag__skills-star {
        color: #FFD700 !important;
        position: absolute;
        top: -6px;
        right: -12px;
        z-index: 20;
        filter: drop-shadow(0 0 3px rgba(0, 0, 0, .9)) drop-shadow(0 0 3px rgba(0, 0, 0, .9));
    }

    /* rozmery doladeny na 343x742 (viz CLAUDE.md "Testovaci rozliseni"), aby vsech 8 skillu vyslo bez scrollovani */
    .skill-hint-text {
        font-size: 0.85em;
        margin-bottom: 6px;
        text-align: left;
    }

    .skill-hint-text p {
        margin: 0 0 4px;
    }

    .skill-hint-text p:last-child {
        margin-bottom: 0;
    }

    .skill-dialog-stats {
        display: grid;
        grid-template-columns: auto auto;
        column-gap: 10px;
        row-gap: 2px;
        justify-content: start;
        text-align: left;
        font-size: 1.05rem;
        font-weight: 500;
        padding: 6px 0 4px;
    }

    .skill-dialog-stats__value {
        font-weight: 700;
    }

    .theme--light .skill-dialog-fixed-text {
        color: rgba(0, 0, 0, .87);
    }
    .theme--dark .skill-dialog-fixed-text {
        color: rgba(255, 255, 255, .87);
    }
    .skill-dialog-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding-bottom: var(--responsive-dialog-close-clearance) !important;
    }

    .locked-stone-dialog-bg {
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
    /* !important nutne - Vuetify ".v-dialog > .v-card > .v-card__text" ma vyssi specificitu (stejny nalez jako u Stones.vue .stones-dialog-content) */
    .locked-stone-dialog-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding-bottom: var(--responsive-dialog-close-clearance) !important;
    }

    .skill-buy-list {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .skill-buy-row {
        position: relative;
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 6px 10px;
        padding: 0 2px;
        margin-bottom: 3px;
    }

    .skill-buy-row__clickable {
        display: flex;
        flex-wrap: nowrap;
        flex: 1 1 auto;
        align-items: center;
        gap: 6px 8px;
        min-width: 0;
        padding: 3px 6px;
        border-radius: 6px;
        cursor: pointer;
        background-color: rgba(0, 0, 0, .05);
        border: 1px solid rgba(0, 0, 0, .08);
        transition: background-color .15s ease;
    }

    /* zrcadleni pro praváky (flag off = Buy vpravo, dal od palce leváka) - stejny princip jako SkillListTable.vue */
    .skill-buy-row__action--left {
        order: -1;
    }

    .skill-buy-row__clickable:hover,
    .skill-buy-row__clickable--active {
        background-color: rgba(0, 0, 0, .15);
    }

    .skill-buy-row__name {
        flex: 1 1 100px;
        min-width: 0;
        font-size: 0.95em;
        white-space: normal;
        word-break: break-word;
    }

    .skill-buy-row__value {
        font-size: 0.95em;
    }

    .skill-buy-row__icon,
    .skill-buy-row__value,
    .skill-buy-row__action {
        flex: 0 0 auto;
    }

    .achieve-list {
        display: flex;
        flex-direction: column;
        font-size: 1.3em;
    }

    /* hvezdicky reaguji na tema primo pres :color prop v sablone, tenhle blok resi jen text */
    .theme--light .achieve-list {
        color: #2b2b2b;
    }
    .theme--dark .achieve-list {
        color: #e0e0e0;
    }

    .achieve-row {
        position: relative;
        margin-bottom: 6px;
    }

    .achieve-row__trigger {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 6px 4px;
        border-radius: 6px;
        cursor: pointer;
        background-color: rgba(0, 0, 0, .05);
        border: 1px solid rgba(0, 0, 0, .08);
        transition: background-color .15s ease;
    }

    .achieve-row__trigger:hover,
    .achieve-row__trigger--active {
        background-color: rgba(0, 0, 0, .15);
    }

    .achieve-row__meta-line {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .achieve-row__stars {
        flex: 0 0 auto;
    }

    .achieve-row__value {
        flex: 0 0 auto;
        margin-left: auto;
    }

    .oak-popup-overlay {
        position: fixed;
        inset: 0;
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: rgba(0, 0, 0, .35);
        cursor: pointer;
    }

    .oak-popup-box {
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: min(90vw, 420px);
        background: rgba(0, 0, 0, .75);
        backdrop-filter: blur(2px);
        color: #fff;
        padding: 18px 20px;
        border-radius: 12px;
        font-size: 1.1em;
        text-align: left;
        box-shadow: 0 8px 24px rgba(0, 0, 0, .4);
    }

    .oak-popup-box__title {
        font-weight: 700;
        font-size: 1.15em;
    }

    .oak-popup-box__body {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .oak-popup-box__text p {
        margin: 0 0 6px;
    }

    .oak-popup-box__text p:last-child {
        margin-bottom: 0;
    }
</style>
